import { View } from "react-native";
import React from "react";
import { useAppSelector } from "hooks/useAppSelector";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  initialChildUser,
  setEditingChild,
  setEditingUser,
  setPrimaryUser,
  setSecondaryUsers,
  setStep,
} from "features/slices/buyFlowSlice";
import { BuyFlowUser, PrimaryUser } from "features/types";
import Button from "components/Button";
import { FontAwesome5, MaterialCommunityIcons } from "@expo/vector-icons";
import { Card, Divider, Text } from "@ui-kitten/components";
import ProfileAvatar from "components/ProfileAvatar";
import QuantitySelector from "./QuantitySelector";
import { useRoute } from "@react-navigation/native";
import env from "config/env";
import i18n from "plugins/localization";
import { MAX_ACCOUNT_PLAN_PACKAGE_COUNT } from "constants/";
import { getTotalPlanPackageCount } from "../registerUtils";
import { getUserName } from "utils/userUtils";
import { getPlanType } from "utils/planPackageUtils";
interface RenderUserDetailProps {
  user: BuyFlowUser;
  setQuantity: (quantity: number) => void;
  onRemove?: (index: number) => void;
  index: number;
}

interface UserDetailsProps {
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingMemberIndex: React.Dispatch<React.SetStateAction<number>>;
}
const UserDetails: React.FC<UserDetailsProps> = ({
  setVisible,
  setEditingMemberIndex,
}) => {
  const {
    primaryUser,
    secondaryUsers,
    selectedPlanPackage,
    selectedBillingInterval,
  } = useAppSelector((state) => state.buyFlow);
  const dispatch = useAppDispatch();
  if (!selectedPlanPackage) return null;

  const totalPlanPackageCount = getTotalPlanPackageCount(
    primaryUser,
    secondaryUsers
  );

  const updateSecondaryUserQuantity = (index: number, newQuantity: number) => {
    const newUsers = secondaryUsers.map((user, i) => {
      if (i === index) {
        return { ...user, quantity: newQuantity };
      }
      return user;
    });
    dispatch(setSecondaryUsers(newUsers));
  };

  const removeSecondaryUser = (index: number) => {
    const newUsers = secondaryUsers.filter((_, i) => i !== index);
    dispatch(setSecondaryUsers(newUsers));
  };

  const handleEditBtnPress = (user: BuyFlowUser, index: number) => {
    if (user.hasOwnProperty("password")) {
      // if editing primary user, go back to step 1
      dispatch(setStep(1));
      return;
    }
    dispatch(setEditingUser(user));
    setEditingMemberIndex(index);
    if (user.minor) {
      // if editing child user
      dispatch(setEditingChild(true));
    } else {
      // editing regular secondary user
      dispatch(setEditingChild(false));
    }
    setVisible(true);
  };

  const handleAddMemberBtnPress = (index: number) => {
    setEditingMemberIndex(index);
    dispatch(setEditingChild(false));
    dispatch(setEditingUser(null));
    setVisible(true);
  };

  const handleAddChildBtnPress = (index: number) => {
    setEditingMemberIndex(index);
    dispatch(setEditingChild(true));
    dispatch(setEditingUser(initialChildUser));
    setVisible(true);
  };

  const RenderUserDetail: React.FC<RenderUserDetailProps> = ({
    user,
    onRemove,
    index,
    setQuantity,
  }) => {
    const route = useRoute();
    const { signupCharge } = useAppSelector((state) => state.buyFlow);
    const imageIdentifier = (user as PrimaryUser).id
      ? `${env.fileUrl}/${user.imageIdentifier}`
      : user.imageIdentifier;

    return (
      <View testID="register-user-detail">
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <View>
            <View style={{ flexDirection: "row", alignItems: "flex-end" }}>
              <ProfileAvatar
                source={{ uri: imageIdentifier! }}
                hasImageIdentifier={!!imageIdentifier}
                size={50}
              ></ProfileAvatar>
              <View style={{ marginLeft: 10 }}>
                <Text category="h6">
                  {getUserName(user)}
                  {user.minor ? ` (${i18n.t("screens.Register.child")})` : ""}
                </Text>
                <View
                  style={{
                    marginTop: 4,
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {/* do not show edit button if user is primary and is in account
                  setup page */}
                  {route.name === "AccountSetup" && index === -1 ? null : (
                    <>
                      <Text
                        category="c2"
                        onPress={() => handleEditBtnPress(user, index)}
                      >
                        {i18n.t("screens.Register.editButton")}
                      </Text>
                      {onRemove && <Text> </Text>}
                    </>
                  )}

                  {onRemove && (
                    <View
                      style={{ flexDirection: "row", alignItems: "center" }}
                    >
                      <Text
                        category="c2"
                        onPress={() => onRemove && onRemove(index)}
                      >
                        Remove
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 5,
                marginLeft: 45,
              }}
            >
              <View style={{ width: 50, alignItems: "center" }}>
                <FontAwesome5 name="passport" size={24} color="black" />
              </View>
              <View style={{ marginLeft: 10 }}>
                <Text category="c2">{selectedPlanPackage.name}</Text>
              </View>
            </View>
            {getPlanType(selectedPlanPackage) === "VOUCHER" && (
              <View style={{ marginLeft: 50 }}>
                <QuantitySelector
                  quantity={user.quantity!}
                  setQuantity={setQuantity}
                />
              </View>
            )}
          </View>
        </View>
      </View>
    );
  };

  return (
    <View>
      <View className="mt-4">
        <RenderUserDetail
          user={primaryUser}
          setQuantity={(newQuantity) =>
            dispatch(setPrimaryUser({ ...primaryUser, quantity: newQuantity }))
          }
          index={-1}
        />
        <View style={{ marginVertical: 20 }}></View>

        {secondaryUsers.map((user, index) => (
          <View key={user.email + user.firstName + user.lastName + index}>
            <RenderUserDetail
              user={user}
              setQuantity={(newQuantity) =>
                updateSecondaryUserQuantity(index, newQuantity)
              }
              onRemove={(email) => removeSecondaryUser(email)}
              index={index}
            />
            {index < secondaryUsers.length - 1 && (
              <Divider style={{ marginVertical: 10, marginLeft: 20 }}></Divider>
            )}
          </View>
        ))}
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "center",
          marginVertical: 15,
        }}
      >
        {totalPlanPackageCount <= MAX_ACCOUNT_PLAN_PACKAGE_COUNT && (
          <>
            {/* for kids club memberships, some tenants don't want the add member button to show
          because it is confusing to the user. */}
            {!selectedPlanPackage.disableAddAdultInBuyFlow && (
              <Button
                size="small"
                onPress={() => handleAddMemberBtnPress(-1)}
                style={{ marginRight: 10 }}
                testID="register-add-member-button"
              >
                Add Member
              </Button>
            )}
            {!selectedPlanPackage.disableAddChildInBuyFlow && (
              <Button
                size="small"
                onPress={() => handleAddChildBtnPress(-1)}
                testID="register-add-child-button"
              >
                Add Child
              </Button>
            )}
          </>
        )}
      </View>

      {!selectedPlanPackage.disableAddAdultInBuyFlow && (
        <Card
          style={{
            backgroundColor: "#f5f5f5",
            paddingVertical: 5,
            borderRadius: 12,
          }}
          onPress={() => handleAddMemberBtnPress(-1)}
        >
          <Text style={{ textAlign: "center", marginBottom: 10 }}>
            <MaterialCommunityIcons
              name="account-plus-outline"
              size={50}
              color="black"
            />
          </Text>
          <Text
            style={{
              textAlign: "center",
              marginBottom: 10,
              color: "rgba(0,0,0,0.87)",
            }}
          >
            {i18n.t("screens.Register.purchaseAdditionalMemberships")}
          </Text>
        </Card>
      )}
    </View>
  );
};

export default UserDetails;
